import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  CrmObjectInterface,
  DateDefault,
  DeleteCrmObjectRequestInterface,
  FilterGroupOperator,
  FilterInterface,
  FilterOperator,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListActivitiesResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFieldOptionsResponseInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  SortDirection,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
} from '@vendasta/crm';
import { map, Observable, switchMap, take } from 'rxjs';
import { AbstractCrmObjectService } from './_abstract.service';
import { ContactService } from './_contact.service';
import { CompanyService } from './_company.service';
import { GalaxyDateDefault, GalaxyFilterInterface, GalaxyFilterOperator } from '@vendasta/galaxy/filter/chips';
import {
  FilterGroupInterface,
  FilterValueInterface,
} from '@vendasta/crm/lib/_internal/interfaces/galaxy-filters.interface';
import { ActivityObjectService } from './_activity_object.service';
import { SortByInterface } from '@vendasta/crm/lib/_internal/interfaces/crm-objects.interface';
import { MatSort } from '@angular/material/sort';
import { CrmInjectionToken, CrmDependencies, ObjectType } from '../../../tokens-and-interfaces';

@Injectable()
export class CrmObjectService {
  private handledObjectTypes: ObjectType[] = ['Company', 'Contact', 'Activity'];
  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly contactService: ContactService,
    private readonly companyService: CompanyService,
    private readonly activityService: ActivityObjectService,
  ) {}

  createCrmObject(objectType: ObjectType, crmObject: CrmObjectInterface): Observable<CreateCrmObjectResponseInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: CreateCrmObjectRequestInterface = {
          namespace: namespace,
          crmObject: crmObject,
        };
        return this.selectService(objectType).createCrmObject(request);
      }),
      take(1),
    );
  }

  deleteCrmObject(objectType: ObjectType, objectId: string): Observable<HttpResponse<null>> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: DeleteCrmObjectRequestInterface = {
          namespace: namespace,
          crmObjectId: objectId,
        };
        return this.selectService(objectType).deleteCrmObject(request);
      }),
      take(1),
    );
  }

  validateCrmObject(
    objectType: ObjectType,
    crmObject: CrmObjectInterface,
  ): Observable<ValidateCrmObjectResponseInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: ValidateCrmObjectRequestInterface = {
          namespace: namespace,
          crmObject: crmObject,
        };
        return this.selectService(objectType).validateCrmObject(request);
      }),
      take(1),
    );
  }

  updateCrmObject(objectType: ObjectType, crmObject: CrmObjectInterface): Observable<HttpResponse<null>> {
    const paths: string[] = [];
    crmObject.fields?.forEach((field) => {
      paths.push(`fields.${field.fieldId}`);
    });

    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: UpdateCrmObjectRequestInterface = {
          namespace: namespace,
          crmObject: crmObject,
          fieldMask: {
            paths: paths,
          },
        };
        return this.selectService(objectType).updateCrmObject(request);
      }),
      take(1),
    );
  }

  convertSortBy(sorting: MatSort[] | undefined): SortByInterface[] | undefined {
    if (!sorting) {
      return undefined;
    }
    const sortBy: SortByInterface[] = [];

    for (const sort of sorting) {
      if (sort.direction === '') {
        return;
      }
      if (sort.direction === 'asc') {
        sortBy.push({
          fieldId: sort.active,
          sortDirection: SortDirection.SORT_DIRECTION_ASCENDING,
        });
      }
      if (sort.direction === 'desc') {
        sortBy.push({
          fieldId: sort.active,
          sortDirection: SortDirection.SORT_DIRECTION_DESCENDING,
        });
      }
    }
    return sortBy;
  }

  convertFilters(filters: GalaxyFilterInterface[] | undefined): FilterGroupInterface | undefined {
    if (!filters) {
      return undefined;
    }
    const filterGroup = {} as FilterGroupInterface;
    filterGroup.operator = FilterGroupOperator.FILTER_GROUP_OPERATOR_AND;
    filterGroup.filters = [];
    filters.forEach((inputFilter) => {
      if (!inputFilter.fieldId || !inputFilter.operator) {
        return;
      }
      const filterOperator: FilterOperator = inputFilter.operator
        ? FilterOperator[GalaxyFilterOperator[inputFilter.operator]]
        : undefined;
      const filterGroupFilter: FilterInterface = {
        fieldId: inputFilter.fieldId,
        operator: filterOperator,
        values: [],
        floatTolerance: inputFilter.floatTolerance,
      };
      filterGroupFilter.values = inputFilter.values?.map((v) => {
        const dateDefault: DateDefault = v.dateDefault ? DateDefault[GalaxyDateDefault[v.dateDefault]] : undefined;
        return {
          boolean: v.boolean,
          integer: v.integer,
          string: v.string,
          date: v.date,
          dateDefault: dateDefault,
          float: v.float,
        } as FilterValueInterface;
      });
      filterGroup.filters?.push(filterGroupFilter);
    });
    return filterGroup;
  }

  listObjects(
    objectType: ObjectType,
    request: ListCrmObjectsRequestInterface,
  ): Observable<ListCrmObjectsResponseInterface> {
    return this.config.namespace$.pipe(
      switchMap((namespace) =>
        this.selectService(objectType)
          .listObjects({ ...request, namespace: namespace })
          .pipe(
            take(1),
            map((response) => {
              if (objectType === 'Activity') {
                response.crmObjects = (response as ListActivitiesResponseInterface).activities;
              }
              return response;
            }),
          ),
      ),
    );
  }

  listObjectsFilters(
    objectType: ObjectType,
    request: ListFiltersRequestInterface,
  ): Observable<ListFiltersResponseInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.selectService(objectType)
          .listObjectsFilters({ ...request, namespace: namespace })
          .pipe(take(1)),
      ),
    );
  }

  getMultiObject(objectType: ObjectType, crmObjectIds: string[]): Observable<GetMultiCrmObjectResponseInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace: string) => {
        const request: GetMultiCrmObjectRequestInterface = {
          namespace: namespace,
          crmObjectIds: crmObjectIds,
        };
        return this.selectService(objectType).getMultiObject(request);
      }),
      take(1),
    );
  }

  listFieldOptions(
    objectType: ObjectType,
    request: ListFieldOptionsRequestInterface,
  ): Observable<ListFieldOptionsResponseInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.selectService(objectType).listFieldOptions({
          ...request,
          namespace: namespace,
        }),
      ),
      take(1),
    );
  }

  private selectService(objectType: ObjectType): AbstractCrmObjectService {
    if (!this.handledObjectTypes.includes(objectType)) {
      console.error('Object Type unhandled. The CRM Object Service will be undefined');
    }

    switch (objectType) {
      case 'Contact':
        return this.contactService;
      case 'Company':
        return this.companyService;
      case 'Activity':
        return this.activityService;
    }
  }
}
